import React from "react";
import {
  Modal,
  Typography,
  Avatar,
  Tag,
  Divider,
  Row,
  Col,
  Space,
  Descriptions,
} from "antd";
import {
  UserOutlined,
  WarningOutlined,
  FileSearchOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Title, Text, Paragraph } = Typography;

const ORANGE = "#e2700d";

const severityColor = {
  LOW: "green",
  MEDIUM: "orange",
  HIGH: "red",
};

const statusColor = {
  PENDING: "orange",
  IN_REVIEW: "blue",
  RESOLVED: "green",
};

const renderEntityPreview = (entityType, entity) => {
  if (!entity) return <Text type="secondary">No entity data found</Text>;

  /* ===== USER ===== */
  if (entityType === "USER") {
    return (
      <Row gutter={16} align="middle">
        <Col>
          <Avatar size={64} src={entity.avatar} icon={<UserOutlined />} />
        </Col>
        <Col>
          <Text strong>{entity.name}</Text>
          <br />
          <Text type="secondary">{entity.email}</Text>
        </Col>
      </Row>
    );
  }

  /* ===== GROUP ===== */
  if (entityType === "GROUP") {
    return (
      <Row gutter={16} align="middle">
        <Col>
          <Avatar
            size={64}
            shape="square"
            src={entity.image}
            icon={<FileSearchOutlined />}
          />
        </Col>
        <Col>
          <Text strong>{entity.name}</Text>
          <br />
          <Text type="secondary">{entity.region}</Text>
          <br />
          <Space size="small">
            <Tag color="blue">Members: {entity.members?.length || 0}</Tag>
            <Tag color="green">{entity.status}</Tag>
          </Space>
        </Col>
      </Row>
    );
  }

  /* ===== POST / VIDEO ===== */
  if (entityType === "POST") {
    return (
      <>
        {entity.mediaUrl && (
          <>
            {entity.mediaType === "video" ? (
              <video
                src={entity.mediaUrl}
                controls
                style={{
                  width: "100%",
                  maxHeight: 260,
                  borderRadius: 8,
                }}
              />
            ) : (
              <img
                src={entity.mediaUrl}
                alt="Post"
                style={{
                  width: "100%",
                  maxHeight: 260,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
            )}
          </>
        )}

        <Text style={{ marginLeft: "20px" }} strong>
          {entity.postId}
        </Text>

        {entity.caption && (
          <Paragraph style={{ marginTop: 12 }}>{entity.caption}</Paragraph>
        )}
      </>
    );
  }

  return <Text type="secondary">Unsupported entity type</Text>;
};

const ViewReport = ({ open, onClose, report }) => {
  if (!report) return null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={720}
      bodyStyle={{ background: "#fff", padding: 24 }}
      closeIcon={<span style={{ color: ORANGE, fontSize: 18 }}>×</span>}
    >
      {/* 🔹 HEADER */}
      <Row gutter={16} align="middle">
        <Col>
          <Avatar
            size={64}
            icon={<WarningOutlined />}
            style={{ backgroundColor: ORANGE }}
          />
        </Col>

        <Col flex="auto">
          <Title level={4} style={{ marginBottom: 0 }}>
            Report Details
          </Title>
          <Text type="secondary">Report ID: {report.reportId}</Text>
        </Col>

        <Col>
          <Space>
            <Tag color={severityColor[report.severity]}>{report.severity}</Tag>
            <Tag color={statusColor[report.status]}>{report.status}</Tag>
          </Space>
        </Col>
      </Row>

      <Divider />

      {/* 🔹 REPORT INFO */}
      <Descriptions
        bordered
        size="small"
        column={1}
        labelStyle={{ fontWeight: 600 }}
      >
        <Descriptions.Item label="Entity Type">
          <Tag icon={<FileSearchOutlined />} color="blue">
            {report.entityType}
          </Tag>
        </Descriptions.Item>

        {/* <Descriptions.Item label="Entity ID">
          <Text copyable>{report.entityId}</Text>
        </Descriptions.Item> */}

        <Descriptions.Item label="Entity ID">
          <Text copyable>
            {(() => {
              if (!report?.entityType || !report?.entityDetails) return "N/A";

              switch (report.entityType) {
                case "USER":
                  return report.entityDetails.username || "N/A";

                case "POST":
                  return report.entityDetails.postId || "N/A";

                case "GROUP":
                  return report.entityDetails.name || "N/A";

                default:
                  return report.entityId;
              }
            })()}
          </Text>
        </Descriptions.Item>

        <Descriptions.Item label="Reason">
          <Paragraph style={{ marginBottom: 0 }}>{report.reason}</Paragraph>
        </Descriptions.Item>
      </Descriptions>
      {/* 🔹 ENTITY PREVIEW */}
      <Divider orientation="left" style={{ color: ORANGE }}>
        Entity Preview
      </Divider>

      {renderEntityPreview(report.entityType, report.entityDetails)}

      {/* 🔹 REPORTER */}
      <Divider orientation="left" style={{ color: ORANGE }}>
        Reporter Details
      </Divider>

      <Row gutter={16} align="middle">
        <Col>
          <Avatar
            size={48}
            src={report.reporterDetails?.avatar}
            icon={<UserOutlined />}
          />
        </Col>
        <Col>
          <Text strong>{report.reporterDetails?.name}</Text>
          <br />
          <Text type="secondary">{report.reporterDetails?.email}</Text>
        </Col>
      </Row>

      {/* 🔹 DATES */}
      <Divider />
      <Text type="secondary" style={{ fontSize: 12 }}>
        Created: {moment(report.createdAt).format("DD MMM YYYY, hh:mm A")}
      </Text>
    </Modal>
  );
};

export default ViewReport;
